// Authenticator — the portal's second-factor code, generated by Atlas
// (docs/portal-authenticators.md).
//
// A super_admin enrols the portal's secret once (the key under the QR code, or the
// otpauth URI); the server encrypts it with KMS and keeps the ciphertext. Everyone
// with access to the practice can press "Show code": the code is fetched, shown
// with a countdown and a copy button, and dropped from state when the countdown
// ends. Every reveal is audited server-side. Nothing here logs: not the key pasted
// into the enrol form (type=password, cleared after submit), not the code.
//
// When a portal session is live, the panel next to this card offers "Type code into
// session", which never shows the code at all; that is the preferred path.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  enrollAuthenticator,
  fetchAuthenticators,
  fetchAuthenticatorCode,
  revokeAuthenticator,
} from "./portalAuthenticatorApi";
import { relativeTime } from "./ageing";
import "./Queue.css";

// The code as a person reads it off a phone: "123 456".
export function groupedCode(code) {
  if (typeof code !== "string" || code.length < 6) return code || "";
  const half = Math.ceil(code.length / 2);
  return `${code.slice(0, half)} ${code.slice(half)}`;
}

function AuthenticatorRow({ practiceId, authenticator, isSuperAdmin, onRevoked, onError }) {
  const [revealed, setRevealed] = useState(null); // {code, secondsRemaining}
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const timer = useRef(null);

  const clear = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    setRevealed(null);
    setCopied(false);
  }, []);

  useEffect(() => clear, [clear]);

  const show = async () => {
    if (loading) return;
    setLoading(true);
    onError(null);
    try {
      const out = await fetchAuthenticatorCode(practiceId, authenticator.portalKey);
      clear();
      setRevealed(out);
      timer.current = setInterval(() => {
        setRevealed((current) => {
          if (!current) return current;
          if (current.secondsRemaining <= 1) {
            clearInterval(timer.current);
            timer.current = null;
            return null;
          }
          return { ...current, secondsRemaining: current.secondsRemaining - 1 };
        });
      }, 1000);
    } catch (err) {
      onError(
        err.status === 429
          ? "Too many code requests; wait a minute and try again."
          : err.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!revealed) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(revealed.code);
        setCopied(true);
      }
    } catch (err) {
      onError("The code could not be copied; read it from the screen.");
    }
  };

  const revoke = async () => {
    if (revoking) return;
    if (
      typeof window !== "undefined" &&
      window.confirm &&
      !window.confirm(
        `Revoke the ${authenticator.portalLabel} authenticator? Nobody will be able to get a code from Atlas until a new secret is enrolled.`,
      )
    ) {
      return;
    }
    setRevoking(true);
    onError(null);
    try {
      clear();
      await revokeAuthenticator(practiceId, authenticator.portalKey);
      onRevoked(authenticator);
    } catch (err) {
      onError(err.message);
    } finally {
      setRevoking(false);
    }
  };

  return (
    <tr data-testid="authenticator-row" data-portal={authenticator.portalKey}>
      <td>{authenticator.portalLabel}</td>
      <td>{authenticator.label}</td>
      <td className="sq-muted">
        {authenticator.createdByEmail || "—"}
        {authenticator.createdAt ? `, ${relativeTime(authenticator.createdAt)}` : ""}
      </td>
      <td className="sq-muted">
        {authenticator.lastUsedAt ? relativeTime(authenticator.lastUsedAt) : "never"}
      </td>
      <td className="sq-nowrap">
        {revealed ? (
          <span className="sq-auth-code" data-testid="authenticator-code">
            <code>{groupedCode(revealed.code)}</code>
            <span className="sq-muted sq-auth-countdown">
              {revealed.secondsRemaining}s
            </span>
            <button className="btn btn-secondary btn-sm" onClick={copy} type="button">
              {copied ? "Copied" : "Copy"}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={clear} type="button">
              Hide
            </button>
          </span>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={show} disabled={loading} type="button">
            {loading ? "Getting code…" : "Show code"}
          </button>
        )}
        {isSuperAdmin && (
          <button
            className="btn btn-secondary btn-sm sq-auth-revoke"
            onClick={revoke}
            disabled={revoking}
            type="button"
          >
            {revoking ? "Revoking…" : "Revoke"}
          </button>
        )}
      </td>
    </tr>
  );
}

export default function AuthenticatorCard({ practiceId, isSuperAdmin = false, readOnly = false }) {
  const [overview, setOverview] = useState(null); // {enabled, portals, authenticators}
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [portal, setPortal] = useState("");
  const [label, setLabel] = useState("");
  const [secret, setSecret] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const alive = useRef(true);

  const load = useCallback(async () => {
    if (!practiceId) return;
    try {
      const body = await fetchAuthenticators(practiceId);
      if (!alive.current) return;
      setOverview(body);
      setPortal((current) =>
        current && body.portals.some((p) => p.key === current)
          ? current
          : body.portals[0]?.key || "",
      );
    } catch (err) {
      if (!alive.current) return;
      setError(err.message);
    }
  }, [practiceId]);

  useEffect(() => {
    alive.current = true;
    setOverview(null);
    setError(null);
    setNotice(null);
    load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  const enabled = overview ? overview.enabled : true;
  const portals = overview ? overview.portals : [];
  const rows = overview ? overview.authenticators : [];
  const enrolledKeys = new Set(rows.map((r) => r.portalKey));
  const freePortals = portals.filter((p) => !enrolledKeys.has(p.key));
  // The portal the select shows, which is what Enrol sends: the chosen one while
  // it is still free, else the first free one.
  const enrolPortal = freePortals.some((p) => p.key === portal) ? portal : freePortals[0]?.key || "";

  const enroll = async (e) => {
    e.preventDefault();
    if (enrolling || !enrolPortal || !secret.trim()) return;
    setEnrolling(true);
    setError(null);
    setNotice(null);
    const pasted = secret;
    // Cleared before the request resolves: the field never outlives the submit.
    setSecret("");
    try {
      const row = await enrollAuthenticator(practiceId, { portal: enrolPortal, label: label.trim(), secret: pasted });
      setLabel("");
      setNotice(`Enrolled the ${row.portalLabel} authenticator.`);
      load();
    } catch (err) {
      setError(
        err.status === 409
          ? "An authenticator is already enrolled for that portal. Revoke it first to replace it."
          : err.message,
      );
    } finally {
      setEnrolling(false);
    }
  };

  if (!practiceId || readOnly) return null;

  return (
    <section className="sq-ps sq-auth" data-testid="authenticator-card">
      <h3 className="sq-section-title">Authenticator</h3>
      <p className="sq-muted">
        The portal’s sign-in code, generated here instead of on one person’s phone.
        The secret is encrypted with KMS and never shown again; every code shown or
        typed is recorded against your account. Some portals’ terms forbid a shared
        login: that is the practice’s decision to make before a secret is enrolled.
      </p>

      {error && (
        <div className="sq-message error" role="alert">
          <span>{error}</span>
          <button className="sq-message-close" aria-label="Dismiss" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}
      {notice && (
        <div className="sq-message success" role="status">
          <span>{notice}</span>
          <button className="sq-message-close" aria-label="Dismiss" onClick={() => setNotice(null)}>
            ×
          </button>
        </div>
      )}

      {overview && !enabled && (
        <div className="sq-run-banner grey" role="status">
          <span>Portal authenticators are not enabled for this environment.</span>
        </div>
      )}

      {overview && rows.length === 0 && enabled && (
        <p className="sq-muted" data-testid="authenticator-empty">
          No authenticator is enrolled for this practice yet.
        </p>
      )}

      {rows.length > 0 && (
        <div className="sq-table-wrap">
          <table className="sq-table sq-auth-table">
            <thead>
              <tr>
                <th>Portal</th>
                <th>Label</th>
                <th>Enrolled by</th>
                <th>Last used</th>
                <th>Code</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <AuthenticatorRow
                  key={row.id}
                  practiceId={practiceId}
                  authenticator={row}
                  isSuperAdmin={isSuperAdmin}
                  onRevoked={(gone) => {
                    setNotice(`Revoked the ${gone.portalLabel} authenticator.`);
                    load();
                  }}
                  onError={setError}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isSuperAdmin && enabled && overview && (
        <form className="sq-auth-enroll" onSubmit={enroll} data-testid="authenticator-enroll" autoComplete="off">
          <h4 className="sq-auth-subtitle">Enrol a secret</h4>
          {freePortals.length === 0 ? (
            <p className="sq-muted">
              {portals.length === 0
                ? "This practice has no portal to enrol for yet (no submit portal, no runbook)."
                : "Every portal already has an authenticator. Revoke one to replace it."}
            </p>
          ) : (
            <div className="sq-auth-fields">
              <label className="sq-field">
                <span>Portal</span>
                <select
                  value={enrolPortal}
                  onChange={(e) => setPortal(e.target.value)}
                  disabled={enrolling}
                  data-testid="authenticator-portal"
                >
                  {freePortals.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="sq-field">
                <span>Label</span>
                <input
                  type="text"
                  value={label}
                  maxLength={120}
                  placeholder="e.g. billing login"
                  onChange={(e) => setLabel(e.target.value)}
                  disabled={enrolling}
                  data-testid="authenticator-label"
                />
              </label>
              <label className="sq-field sq-auth-secret">
                <span>Key or otpauth:// URI</span>
                <input
                  type="password"
                  value={secret}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="Paste the key shown under the QR code"
                  onChange={(e) => setSecret(e.target.value)}
                  disabled={enrolling}
                  data-testid="authenticator-secret"
                />
              </label>
              <button
                className="btn btn-primary btn-sm"
                type="submit"
                disabled={enrolling || !secret.trim()}
              >
                {enrolling ? "Enrolling…" : "Enrol"}
              </button>
            </div>
          )}
          <p className="sq-field-hint sq-muted">
            In the portal’s two-factor setup choose “can’t scan the code” to get the
            key as text. The key is sent once, encrypted, and cannot be read back.
          </p>
        </form>
      )}
    </section>
  );
}
